import { PinOutlineIcon } from '@carboncopyco/icons';
import type { TransactionSelector } from '@carboncopyco/types';
import { Button } from '@carboncopyco/ui';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object } from 'yup';
import { DescriptionLink } from '../../../DescriptionLink';
import {
  SelectorsField,
  schemaResolver,
  selectorsSchema,
} from '../../../TransactionRuleForm';
import {
  BackButton,
  CancelButton,
  FormLayout,
  Section,
  SubmitButton,
} from '../../../TransactionRuleModal';
import { useElementSize } from '../../../hooks';
import { useCreateTransactionRuleContext } from '../../CreateTransactionRuleContext';
import {
  createInitialSelectors,
  createMatchContentSha256Selector,
  createSelectorsFromScratch,
} from './initialSelectors';
import { getContentSha256Header } from './utils';

const selectorBuilderUrl = 'https://ds-selector-builder.pages.dev/';

export const CreateTransactionSelectorsStep = () => {
  const {
    transactionRule,
    updateTransactionRule,
    selectedTransaction,
    prevStep,
    cancelCreation,
    nextStep,
    onLearnMoreClick,
  } = useCreateTransactionRuleContext();

  const schema = object().shape({
    selectors: selectorsSchema,
  });

  const defaultSelectorsValue = useMemo(
    () =>
      JSON.stringify(
        transactionRule?.selectors ??
          createInitialSelectors(selectedTransaction?.original.request)
      ),
    [transactionRule, selectedTransaction]
  );
  const methods = useForm({
    defaultValues: {
      selectors: !selectedTransaction
        ? JSON.stringify(createSelectorsFromScratch())
        : defaultSelectorsValue,
    },
    resolver: schemaResolver(schema),
  });
  const [isLegalJson, setIsLegalJson] = useState<boolean>(true);

  const submitForm = ({ selectors }: { selectors: TransactionSelector[] }) => {
    updateTransactionRule({ selectors });
    nextStep();
  };

  const selectorsString = methods.watch('selectors');

  const [containerRef, { height }] = useElementSize();

  const setSpecificTransaction = () => {
    const selectors = JSON.parse(selectorsString);

    // if the selector already exists, then we don't want to add it again
    if (
      selectors.find(({ type }) => type === 'MatchContentSha256') ||
      !selectedTransaction
    ) {
      return;
    }

    const matchContentSha256Selector = createMatchContentSha256Selector(
      selectedTransaction.original.response
    );

    methods.setValue(
      'selectors',
      JSON.stringify([...selectors, matchContentSha256Selector], null, 4)
    );
    methods.trigger('selectors');
  };

  const doesContentSha256Exist = !!(
    selectedTransaction &&
    getContentSha256Header(selectedTransaction.original.response)
  );

  return (
    <FormProvider {...methods}>
      <FormLayout onSubmit={methods.handleSubmit(submitForm)}>
        <FormLayout.Content>
          <div className="h-full flex relative">
            {doesContentSha256Exist && (
              <div className="absolute right-0 pt-5">
                <Button
                  blue
                  link
                  label="Set specific transaction"
                  leftIcon={<PinOutlineIcon />}
                  disabled={!isLegalJson}
                  onClick={setSpecificTransaction}
                />
              </div>
            )}
            <Section
              title="Selectors"
              iconType="selectors"
              tooltipContent={
                <DescriptionLink
                  label="Easily create selector using the"
                  linkLabel="Selector Builder"
                  linkUrl={selectorBuilderUrl}
                  onLearnMoreClick={onLearnMoreClick}
                />
              }
            >
              <div
                className="flex-1"
                ref={containerRef}
              >
                <SelectorsField
                  autoFocus
                  hideLabel
                  onError={(errors) => setIsLegalJson(!errors.length)}
                  height={height ?? 428}
                  onLearnMoreClick={onLearnMoreClick}
                />
              </div>
            </Section>
          </div>
        </FormLayout.Content>
        <FormLayout.Footer>
          <BackButton onClick={prevStep} />
          <div className="flex-1" />
          <CancelButton onClick={cancelCreation} />
          <SubmitButton
            label="Next"
            disabled={!isLegalJson || !methods.formState.isValid}
          />
        </FormLayout.Footer>
      </FormLayout>
    </FormProvider>
  );
};
